import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import {
  LakehouseBigQueryConnectionInput,
  LakehouseClouderaConnectionInput,
  LakehouseConnectionTypeEnum,
  LakehouseDatabricksConnectionInput,
  LakehouseMySqlConnectionInput,
  LakehousePostgresConnectionInput,
  LakehouseSnowflakeConnectionInput,
} from '@vendia/management-api-types'
import debug from 'debug'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import Card, { CardRule } from 'src/components/containers/card'
import CheckboxField from 'src/components/fields/checkbox.field'
import RadioGroupField from 'src/components/fields/radio-group.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { getEmptySchema } from 'src/components/schema-designer/utils'
import { isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'
import { GetLakehouseTableSchemaDocument } from '@vendia/control-plane-api'

const logger = debug('app:selectTable')

export const StepSelectTable: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()

  const { connectionId, nodeName, source, uniName, selectedTables } = useStore(form.store, (s) => s.values)

  const getTableSchemaMutation = useMutation({
    mutationFn: async () => {
      const args = {
        tableName: source.selectedTable,
        uniName: uniName!,
        nodeName: nodeName!,
        ...(connectionId && { connectionId }),
      }

      if (source.connection === LakehouseConnectionTypeEnum.Snowflake) {
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            snowflakeConnection: source.snowflake as LakehouseSnowflakeConnectionInput,
          },
        })
      } else if (source.connection === LakehouseConnectionTypeEnum.Cloudera) {
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            clouderaConnection: source.cloudera as LakehouseClouderaConnectionInput,
          },
        })
      } else if (source.connection === LakehouseConnectionTypeEnum.Postgres) {
        logger('Postgres source:', source.postgres)
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            postgresConnection: source.postgres as LakehousePostgresConnectionInput,
          },
        })
      } else if (source.connection === LakehouseConnectionTypeEnum.Mysql) {
        logger('MySql source:', source.mysql)
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            mysqlConnection: source.mysql as LakehouseMySqlConnectionInput,
          },
        })
      } else if (source.connection === LakehouseConnectionTypeEnum.Databricks) {
        logger('Databricks source:', source.databricks)
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            databricksConnection: source.databricks as LakehouseDatabricksConnectionInput,
          },
        })
      } else if (source.connection === LakehouseConnectionTypeEnum.Bigquery) {
        logger('BigQuery source:', source.bigquery)
        return client.graphqlFetch({
          query: GetLakehouseTableSchemaDocument,
          variables: {
            ...args,
            bigqueryConnection: source.bigquery as LakehouseBigQueryConnectionInput,
          },
        })
      }
    },
    onError: (error: Error) => notify.error(`Error fetching table schema: ${error}`),
    onSuccess: (response: any) => {
      logger('response', response)
      if (response.message) {
        notify.error(`Error fetching table schema: ${response.message}`)
        return
      }
      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }
      form.setFieldValue('selectedTableColumns', response.getLakehouseTableSchema)
      form.setFieldValue('schema', JSON.stringify(getEmptySchema()))
      context.goToStep(StepId.TableSettings)
    },
  })

  const selectedTablesList = Object.entries(selectedTables)
    .filter(([_, value]) => value)
    .map(([key]) => key)

  return (
    <StepWrapper>
      <StepContentHeader centered large title='Select source table' description={'Select the table to import.'} />
      <ScrollableStepContent inset autoHeight>
        <Card
          padding='xl'
          className='mx-8 w-full max-w-4xl'
          childrenWrapperClassName='flex w-full flex-grow flex-col gap-4'
          title={`${source.connection} tables`}
        >
          <div className='flex flex-col gap-1'>
            <RadioGroupField
              name='source.selectedTable'
              options={source?.availableTables?.map((table) => ({
                label: table,
                value: table,
              }))}
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.SelectTable,
                validator: isRequiredValidator(),
              })}
              form={form}
              onChange={(selectedTable) => {
                form.setFieldValue('selectedTables', { [selectedTable]: true })
              }}
            />
          </div>
          {source?.availableTables?.length === 0 && (
            <div>No tables available for this connection. Please check your connection settings.</div>
          )}
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit]}>
        {([canSubmit]) => (
          <StepButtons
            context={context}
            isSubmitting={getTableSchemaMutation.isPending}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            nextBlocked={
              !Object.values(selectedTables).some((selected) => selected) ||
              getTableSchemaMutation.isPending ||
              !canSubmit
            }
            onNext={() => {
              if (selectedTablesList.length === 0) {
                notify.error('Please select at least one table')
                return
              }

              try {
                // Single table selection flow
                const previousTable = form.getFieldValue('source.selectedTable')
                const selectedTable = selectedTablesList[0]

                // Reset dependent fields if table selection changed
                if (previousTable && previousTable !== selectedTable) {
                  logger('Selected table changed, resetting form values that depend on columns')
                  form.setFieldValue('product.columnPolicies', [])
                  form.setFieldValue('product.rowPolicies', [])
                  form.setFieldValue('selectedTableColumns', [])
                  form.setFieldValue('lastUpdatedColumn', '')
                  form.setFieldValue('lastUpdatedColumnFormat', '')
                  form.setFieldValue('keyColumns', [])
                }
                form.setFieldValue('source.selectedTable', selectedTable)
                getTableSchemaMutation.mutate()
              } catch (error) {
                logger('Error in table selection:', error)
                notify.error('An unexpected error occurred while processing your selection. Please try again.')
              }
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}

//  TODO: Testing out handling this sort of thing automatically without prompting the user, can add back if needed
// const handleTableChange = async (newTable?: string, previousTable?: string) => {
//   if (previousTable && previousTable !== newTable) {
//     const confirm = window.confirm(
//       'Changing the table will reset the Sharing permissions you have already configured. Are you sure you want to continue?',
//     )
//     if (!confirm) {
//       return
//     }
//     form.setFieldValue('selectedTableColumns', [])
//   }
// }
